import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { MAX_EXPOSURE_TITLE_STYLE } from '@/containers/common/TextStyles'
import { fadeOutFeaturePopupItem } from '@/gameConfigs/components/FeatureIntroPopupComponent'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'

/**
 * Capped-round notice, shown after the feature end popup when the server sends
 * current_round.misc_prizes.capamount_reached = 1.
 *
 * Reuses the shared popup_message_box so it reads as part of the same popup family as the
 * feature intro / free spins summary. MaxExposureState parks the round on
 * maxExposureComplete, so every exit path here must emit it exactly once.
 *
 * Single instance, so all transient state lives at module scope.
 */
const MAX_EXPOSURE_OVERLAY_COLOR = 0x01102a
const MAX_EXPOSURE_OVERLAY_OPACITY = 0.6

const MAX_EXPOSURE_SPINE_SRC = 'popup_message_box'
const MAX_EXPOSURE_ANIM = { in: 'ml_you_won_in', loop: 'ml_you_won_loop', out: 'ml_you_won_out' } as const

//## Explicit break — the copy is designed as two lines, not auto-wrapped.
const MAX_EXPOSURE_TITLE_FALLBACK = 'Maximum exposure\nlimit is reached'

const MAX_EXPOSURE_CONTINUE_STYLE = {
    fontFamily: 'Montserrat_Bold',
    fontSize: 50,
    fill: 0xffffff,
    align: 'center',
}

//## Continue prompt breathes while the box idles. Values match the feature intro popup's
//## continue label (FeatureIntroPopupComponent) so both read identically.
const CONTINUE_PULSE_SCALE = 1.1
const CONTINUE_PULSE_DURATION_S = 0.6

//## The texts reveal off the in clip's completion callback. SpineAnimation.start() returns
//## silently when the skeleton is not ready, and SpineComponent.init() can skip wiring
//## entirely — in both cases that callback never arrives, leaving the popup textless and
//## impossible to dismiss. This is the backstop: reveal anyway shortly after the clip
//## should have ended.
const BOX_OPEN_FALLBACK_MARGIN_S = 0.35
const BOX_OPEN_FALLBACK_DEFAULT_S = 1

// --- transient state (one popup instance) -------------------------------------------------

let continuePulse: gsap.core.Tween | null = null
let boxOpenFallback: gsap.core.Tween | null = null
let hasCompleted = false
let hasBoxOpened = false
let canContinue = false
let isExiting = false
let tapHandler: (() => void) | null = null

const translate = (key: string, fallback: string): string => {
    const translated = TranslationUtils.translate(key)
    return translated === key ? fallback : translated
}

const stopContinuePulse = (element: PIXI.Text | undefined): void => {
    continuePulse?.kill()
    continuePulse = null

    if (element) {
        element.scale.set(1)
    }
}

const startContinuePulse = (element: PIXI.Text): void => {
    stopContinuePulse(element)

    element.scale.set(1)
    continuePulse = gsap.to(element.scale, {
        x: CONTINUE_PULSE_SCALE,
        y: CONTINUE_PULSE_SCALE,
        duration: CONTINUE_PULSE_DURATION_S,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
    })
}

const clearBoxOpenFallback = (): void => {
    boxOpenFallback?.kill()
    boxOpenFallback = null
}

/** Reveals the texts and arms the tap, from whichever of the two paths gets there first. */
const openBoxOnce = (): void => {
    if (hasBoxOpened) {
        return
    }

    hasBoxOpened = true
    clearBoxOpenFallback()
    canContinue = true
    ;(GameSignals as any).maxExposureBoxOpened?.emit()
}

/**
 * Guards against a double emit — the tap handler and any teardown path can both race to
 * finish the popup, and a second emit would end whatever state came next.
 */
const completeOnce = (): void => {
    clearBoxOpenFallback()

    if (hasCompleted) {
        return
    }

    hasCompleted = true
    ;(GameSignals as any).maxExposureComplete?.emit()
}

const resolveParentContainer = (data: ICustomEventData): any => {
    const animation = data.component.componentElement as SpineAnimation
    return data.component.parentContainer || (animation as any)?.parent || animation
}

const detachTapHandler = (parentContainer: any): void => {
    if (tapHandler && parentContainer) {
        parentContainer.off('pointertap', tapHandler)
    }

    tapHandler = null
}

// --- items --------------------------------------------------------------------------------

type PopupTextItemConfig = {
    name: string
    style: Record<string, unknown>
    position: { orientation: Orientation; x: number; y: number }[]
    resolveText: () => string
    /** Continue prompt only — breathes while the box idles. */
    pulse?: boolean
}

/**
 * Title and continue label differ only in copy, style, position and the pulse, so they
 * share one definition — a third label cannot drift out of step with the other two.
 */
const createPopupTextItem = ({ name, style, position, resolveText, pulse = false }: PopupTextItemConfig): any => ({
    name,
    type: AssetType.TEXT,
    translationKey: '',
    // No item-level `anchor`: TextComponent never reads it, so it is set on the element below.
    textStyle: [{ orientation: Orientation.ANY, options: { ...style } }],
    position,
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).showMaxExposure],
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement as PIXI.Text

                data.component.setText(resolveText())
                element.anchor.set(0.5)

                // Stay hidden until the box has opened.
                if (pulse) {
                    stopContinuePulse(element)
                }

                gsap.killTweensOf(element)
                element.visible = false
                element.alpha = 0
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).maxExposureBoxOpened],
            callback: function (data: ICustomEventData) {
                const element = data.component.componentElement as PIXI.Text

                gsap.killTweensOf(element)
                element.visible = true
                element.alpha = 1

                if (pulse) {
                    startContinuePulse(element)
                }
            },
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).maxExposureDismissStarted],
            callback: function (data: ICustomEventData) {
                // The pulse targets element.scale, which killTweensOf(element) inside
                // fadeOutFeaturePopupItem does not cover — stop it explicitly first.
                if (pulse) {
                    stopContinuePulse(data.component.componentElement as PIXI.Text)
                }

                fadeOutFeaturePopupItem(data)
            },
        },
        // Backstop for completions with no dismiss (state fallback timer, error path).
        ...(pulse
            ? [
                  {
                      event: ComponentEvent.CUSTOM_NEW,
                      onGameSignalsTriggered: [(GameSignals as any).maxExposureComplete],
                      callback: function (data: ICustomEventData) {
                          stopContinuePulse(data.component.componentElement as PIXI.Text)
                      },
                  },
              ]
            : []),
    ],
})

export const MAX_EXPOSURE_POPUP_COMPONENT: IComponent = {
    name: ContainersNames.MAX_EXPOSURE_POPUP_CONTAINER,
    type: AssetType.CONTAINER,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 440],
        [Orientation.PORTRAIT, 440],
    ]),
    visible: false,
    events: [
        {
            event: ComponentEvent.SHOW,
            onGameSignalsTriggered: [(GameSignals as any).showMaxExposure],
        },
        {
            event: ComponentEvent.HIDE,
            onGameSignalsTriggered: [(GameSignals as any).maxExposureComplete],
        },
    ],
    items: [
        {
            name: 'Max Exposure Overlay',
            type: AssetType.OVERLAY,
            componentStyle: {
                color: MAX_EXPOSURE_OVERLAY_COLOR,
                opacity: MAX_EXPOSURE_OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).showMaxExposure, ModuleSignals.gameResized],
                    callback: function (data: ICustomEventData) {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            MAX_EXPOSURE_OVERLAY_COLOR,
                            MAX_EXPOSURE_OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: 'Max Exposure Spine',
            type: AssetType.SPINE,
            visible: false,
            src: MAX_EXPOSURE_SPINE_SRC,
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: [MAX_EXPOSURE_ANIM.in as string],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).showMaxExposure],
                    callback: function (data: ICustomEventData) {
                        hasCompleted = false
                        hasBoxOpened = false
                        canContinue = false
                        isExiting = false
                        clearBoxOpenFallback()

                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer = resolveParentContainer(data)

                        // No box to show — release the round rather than parking it forever.
                        if (!parentContainer) {
                            completeOnce()
                            return
                        }

                        detachTapHandler(parentContainer)

                        parentContainer.interactive = true
                        parentContainer.eventMode = 'static'
                        parentContainer.cursor = 'pointer'

                        tapHandler = () => {
                            if (!canContinue || isExiting) {
                                return
                            }

                            isExiting = true
                            clearBoxOpenFallback()

                            //## Emitted before the out clip so the texts fade out in sync with
                            //## the box, matching the feature intro popup's dismiss.
                            ;(GameSignals as any).maxExposureDismissStarted?.emit()
                            animation.start([MAX_EXPOSURE_ANIM.out], [false], 0, [1], completeOnce)
                        }

                        parentContainer.on('pointertap', tapHandler)

                        // SpineComponent.init() already played the in clip once at startup via
                        // animationSettings.default. Clear that track so this is a clean restart
                        // rather than depending on how the wrapper handles replaying a clip.
                        try {
                            animation.animation?.state?.clearTracks()
                            animation.stop()
                        } catch (error) {
                            // ignore — nothing was playing
                        }

                        // Visible immediately, not only on the next frame.
                        animation.visible = true

                        try {
                            animation.start([MAX_EXPOSURE_ANIM.in], [false], 0, [1], () => {
                                if (isExiting) {
                                    return
                                }

                                animation.start([MAX_EXPOSURE_ANIM.loop], [true], 0, [1])
                                openBoxOnce()
                            })
                            requestAnimationFrame(() => {
                                animation.visible = true
                            })
                        } catch (error) {
                            console.warn('[MaxExposurePopup] Failed to play box animation:', error)
                            completeOnce()
                            return
                        }

                        const inClipSeconds =
                            Number((animation as any).animation?.state?.tracks?.[0]?.animation?.duration) ||
                            BOX_OPEN_FALLBACK_DEFAULT_S

                        boxOpenFallback = gsap.delayedCall(inClipSeconds + BOX_OPEN_FALLBACK_MARGIN_S, () => {
                            boxOpenFallback = null

                            if (hasBoxOpened || isExiting) {
                                return
                            }

                            // The in clip's callback never arrived — reveal anyway so the popup
                            // stays readable and dismissible.
                            try {
                                animation.start([MAX_EXPOSURE_ANIM.loop], [true], 0, [1])
                            } catch (error) {
                            }

                            openBoxOnce()
                        })
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).maxExposureComplete],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer = resolveParentContainer(data)

                        clearBoxOpenFallback()
                        detachTapHandler(parentContainer)

                        if (parentContainer) {
                            parentContainer.interactive = false
                            parentContainer.eventMode = 'auto'
                            parentContainer.cursor = 'default'
                        }

                        try {
                            animation.stop()
                        } catch (error) {
                        }

                        animation.visible = false
                    },
                },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 960, y: 357 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 880 },
            ],
        },
        createPopupTextItem({
            name: 'Max Exposure Title',
            style: MAX_EXPOSURE_TITLE_STYLE,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 980, y: 515 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 1035 },
            ],
            resolveText: () => translate('Maximum exposure limit is reached', MAX_EXPOSURE_TITLE_FALLBACK),
        }),
        createPopupTextItem({
            name: 'Max Exposure Continue',
            style: MAX_EXPOSURE_CONTINUE_STYLE,
            position: [
                { orientation: Orientation.LANDSCAPE, x: 960, y: 870 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 1420 },
            ],
            resolveText: () => {
                const key = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
                return translate(key, key)
            },
            pulse: true,
        }),
    ],
}
